<script lang="ts">
  import { userPrefersReducedMotion } from '@pin/dom-utils';
  import type {
    AfterState,
    Alternative,
    PosterState,
    State,
  } from '@pin/schemas/client-state-response';
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { postvoteWithRetries } from '../api/post-vote';
  import voteStore from '../stores/voteStore';
  import { dispatchConfetti } from '../util/confetti';
  import { captureError } from '../util/sentry';
  import { trackEvent } from '../util/snowplow';
  import { setItem } from '../util/storage';
  import ConfettiCharger from './ConfettiCharger.svelte';
  import FocusText from './FocusText.svelte';
  import FurtherNavigation from './FurtherNavigation.svelte';
  import BeforeButton from './VoteButtons/BeforeButton.svelte';
  import SendButton from './VoteButtons/SendButton.svelte';
  import SendingButton from './VoteButtons/SendingButton.svelte';
  import TryAgainButton from './VoteButtons/TryAgainButton.svelte';

  type StateWithArtists = Exclude<State, PosterState | AfterState>;
  export let state: StateWithArtists;
  export let artistId: string;

  let submitting = false;
  let submitError = false;
  let vote: Array<string> = [];
  let submitted = vote.length > 0 ? true : false;

  $: artist = getArtist(artistId, state);
  $: artistImage = getImage(submitted, artist);

  const getArtist = (id: string, state: StateWithArtists) => {
    return state.alternatives.find((alternative) => alternative.id === id);
  };

  const getImage = (submitted: boolean, artist: Alternative | undefined) => {
    if (artist == undefined) {
      return null;
    }
    if (submitted) {
      return artist.extraData.thanksImage;
    }
    return artist.image;
  };

  const getVoteURL = (state: State | undefined) => {
    if (state?.type === 'open' || state?.type === 'closing') {
      return state.voteUrl;
    }
    return null;
  };

  const submit = async () => {
    try {
      const vote = await tryPostVote();
      try {
        dispatchConfetti(500);
        voteStore.set(vote);
        trackEvent(
          { action: 'success:submit', label: artist?.title },
          artist?.id
        );
      } catch (e) {
        // We saw some weird error:submit events
        // in snowplow coming in milliseconds after success:submit
        // so I want to log this and see if we can find out what it was.
        trackEvent({ action: 'error:weird-submit' }, artist?.id);
        captureError('error:weird-submit', e);
      } finally {
        submitted = true;
        submitting = false;
      }
    } catch (e) {
      trackEvent({ action: 'error:submit', label: artist?.title }, artist?.id);
      submitError = true;
      submitting = false;
      throw new Error('could not submit vote');
    }
  };

  const tryPostVote = async (): Promise<Array<string>> => {
    submitting = true;
    submitted = false;
    submitError = false;
    const vote = artist ? [artist.id] : [];
    const voteURL = getVoteURL(state);
    await postvoteWithRetries(voteURL, {
      pollId: state.id,
      systemId: 'mgp-client',
      alternativeIds: vote,
    });
    return vote;
  };

  onMount(() => {
    window.scrollTo({
      top: 0,
      behavior: userPrefersReducedMotion() ? 'auto' : 'smooth',
    });
    if (artist != undefined) {
      setItem('session', 'list-focus-id', `choose-${artist.id}`);
    }
  });
</script>

<svelte:head>
  {#if artist}
    <link rel="preload" as="image" href={artist.extraData.thanksImage.src} />
  {/if}
</svelte:head>

{#if artist != null}
  <div class="card-wrapper">
    <FocusText elementType="h1">
      {#if submitted}
        <div class="small-title">Din stemme gikk til</div>
        <div class="big-title">{artist.title}</div>
      {:else}
        <div class="small-title">Vil du sende</div>
        <div class="big-title">{artist.title}</div>
        <div class="small-title">Til MGP-Finalen?</div>
      {/if}
    </FocusText>
    <div class="image-wrapper">
      <picture>
        <source srcset={artistImage?.src} type="image/webp" />
        <source srcset={artistImage?.fallbackSrc} type="image/png" />
        <img
          height="300"
          width="300"
          src={artistImage?.fallbackSrc}
          alt={artistImage?.alt}
        />
      </picture>
      <div class="gradient"></div>
    </div>
    {#if submitting || !submitted || submitError}
      {#if state.type === 'before'}
        <BeforeButton />
      {:else if submitting}
        <SendingButton />
      {:else if submitError}
        <TryAgainButton {submit} {artist} />
      {:else}
        <SendButton {submit} {artist} />
      {/if}
      <p>Du har kun én stemme, <br />og du kan bruke den én gang per sending</p>
    {:else}
      <!-- svelte-ignore a11y-unknown-role -->
      <div class="thanks-text" role="text">
        <div>takk for din</div>
        <div class="thanks-text-big">Stemme!</div>
      </div>
      <ConfettiCharger />
    {/if}
    <a href="/" class="tertiary" use:link> Tilbake til artistene </a>

    {#if submitted}
      <FurtherNavigation />
    {/if}
  </div>
{/if}

<style>
  .small-title {
    font-weight: 700;
    font-size: 0.9375rem;
    text-transform: initial;
    line-height: 1.1875;
    letter-spacing: 0.02em;
    min-height: 1.1875rem;
  }

  .big-title {
    font-weight: 890;
    font-size: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1.2;
    margin: 0.5rem 0;
  }
  .card-wrapper {
    text-align: center;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 20rem;
    transform: translateZ(0);
  }
  .image-wrapper {
    position: relative;
    z-index: -1;
    transform: translateZ(0);
  }

  .image-wrapper img {
    width: 100%;
    height: auto;
  }

  .gradient {
    position: absolute;
    top: -50px;
    left: -20px;
    bottom: 0;
    right: -20px;
    z-index: -1;
    background: radial-gradient(
      50% 50% at 50% 50%,
      #4e1b52 42.19%,
      rgba(78, 27, 82, 0.829394) 50.99%,
      rgba(78, 27, 82, 0.613636) 62.97%,
      rgba(78, 27, 82, 0.6) 64.99%,
      rgba(78, 27, 82, 0.436102) 71.82%,
      rgba(78, 27, 82, 0) 100%
    );
  }
  p {
    margin: 1rem 0;
  }

  .thanks-text {
    margin-top: -6.75rem;
    font-weight: 700;
    font-size: 1.5625rem;
    line-height: 1.68;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    text-shadow:
      4px 0px 4px rgba(0, 0, 0, 0.5),
      -4px 0px 4px rgba(0, 0, 0, 0.5),
      0px 4px 4px rgba(0, 0, 0, 0.5),
      0px -4px 4px rgba(0, 0, 0, 0.5),
      2px 0px 18px #040104,
      0px 2px 18px #040104;
  }

  .thanks-text-big {
    margin: -0.8rem 0 1rem;
    font-size: 2.4375rem;
    letter-spacing: 0.2em;
  }

  .tertiary {
    text-transform: uppercase;
    font-weight: 600;
    display: grid;
    place-items: center;
    padding: 0;
  }
</style>
